import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Tooltip,
  IconButton,
} from '@mui/material';
import MuiChip from 'app/design/uiComponents/MuiChip';
import BarChartIcon from '@mui/icons-material/BarChart';
import { styled } from '@mui/material/styles';
import { getColorByNum, formatNumber } from 'utils/GenericFunctions';
import { useGetMediaQueryUp } from 'app/hooks/useGetMediaQuery';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { PortfolioBot } from 'types/ApiServicesTypes';
import RealisedPopover from 'app/design/speedBot/ComponentItem/Portfolio/RealisedPopover';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useCalcuatePortfolioSummaryUIData } from 'app/design/speedBot/ComponentItem/Portfolio/PortfolioSummary';


// Styled box for value display (like Max Profit/Max Loss)
const StatBox = styled(Box)(({ theme }) => ({
  flex: 1,
  background: theme.palette.grey[50],
  borderRadius: 10,
  padding: theme.spacing(1.5, 1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: 100,
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
}));

interface PortfolioStatsCardProps {
  title: string;
  botCount: number;
  positions: { open: number; total: number };
  todaysChange: number;
  currencySymbol: string;
  portfolioValue: number;
  totalPL: number;
  selected: boolean;
  onClick: () => void;
  chipLabel?: string;
  maxTitleWidth?: number;
  initialCapital: number;
  netProfit: number;
  bots: PortfolioBot[];
}

const PortfolioStatsCard: React.FC<PortfolioStatsCardProps> = ({
  title,
  botCount,
  positions,
  todaysChange,
  currencySymbol,
  portfolioValue,
  totalPL,
  selected,
  onClick,
  chipLabel,
  maxTitleWidth = 180,
  initialCapital,
  netProfit,
  bots,
}) => {
  // Use media query to determine formatting
  const isLgUp = useGetMediaQueryUp('lg');
  const isNumberFormatting = !isLgUp;
  const decimalPoints = isNumberFormatting ? 2 : 0;

  // Helper for sign
  const getSign = (num?: number) => (num && num > 0 ? '+' : '');
  // Helper for arrow
  const getArrowDirByNum = (num: number) =>
    num > 0 ? '▲' : num < 0 ? '▼' : '';

  // Popover state
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => setAnchorEl(null);
  // Calculate realised/unrealised data
  const { realisedUnrealisedData } = useCalcuatePortfolioSummaryUIData({
    bots,
    symbols: [],
    positionData: [],
    data: {
      todays_change: 0,
      total_profit_loss: 0,
      total_profit_loss_per: 0,
      initial_capital: 0,
      portfolio_value: 0,
      bots: { active: bots }
    }
  });

  return (
    <Box sx={{ width: 400, flex: '0 0 auto' }}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          background: theme => theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          justifyContent: 'space-between',
          border: selected ? '2px solid #1976d2' : undefined,
          boxShadow: selected ? 4 : 2,
          cursor: 'pointer',
        }}
        onClick={onClick}
      >
        {/* Card Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 2,
            pb: 0,
            background: theme => theme.palette.primary.dark[50],
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <BarChartIcon color="primary" sx={{ fontSize: 28 }} />
            <Tooltip title={title} placement="top" arrow>
              <Typography
                variant="h6"
                fontWeight={500}
                color="primary"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: maxTitleWidth,
                  display: 'block',
                  cursor: 'pointer',
                }}
              >
                {title}
              </Typography>
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <MuiChip
              label={chipLabel || `${botCount} Bot${botCount > 1 ? 's' : ''}`}
              color="infolight"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>
        {/* Card Content */}
        <CardContent sx={{ pt: 0.5, pb: 2 }}>
          <Grid container spacing={1}>
            
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <StatBox>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Today’s Change
                    </Typography>
                    <Tooltip title="P&L Summary">
                      <span>
                        <IconButton size="small" onClick={handleInfoClick}>
                          <InfoOutlined fontSize="small" sx={{ cursor: 'pointer' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={400}
                    color="success.main"
                  >
                    {todaysChange === 0 ? '' : getArrowDirByNum(todaysChange)}
                    {currencySymbol}
                    {formatNumber(
                      todaysChange,
                      isNumberFormatting,
                      decimalPoints,
                    )}{' '}
                  </Typography>
                </StatBox>
                {/* <Typography variant="body2" color={getColorByNum(todaysChange)}>
                  <strong>Today&apos;s Change:</strong>{' '}
                  {todaysChange === 0 ? '' : getArrowDirByNum(todaysChange)}
                  {currencySymbol}
                  {formatNumber(
                    todaysChange,
                    isNumberFormatting,
                    decimalPoints,
                  )}
                </Typography> */}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                justifyContent={'space-between'}
              >
                <Typography variant="body3" color="text.secondary">
                  Positions
                </Typography>
                <Typography variant="body2">
                  {/* {positions.open} / {positions.total} */}
                  <MuiChip
                    label={`${positions.open} / ${positions.total}`}
                    size="xsmall"
                    variant="filled"
                    color="greylight"
                    sx={{
                      fontWeight: 400,
                    }}
                  />
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                justifyContent={'space-between'}
              >
                <Typography variant="body3" color="text.secondary">
                  Portfolio Value
                </Typography>
                <Box display={"flex"} alignItems={"center"}>
                <Typography variant="body2">
                  {currencySymbol}
                  {formatNumber(initialCapital, true, decimalPoints)}
                </Typography>
                  <ArrowForwardIcon sx={{ fontSize: '12px', mx: 0.5 }} />
                   <Typography variant="body2" fontWeight={500} color={getColorByNum(netProfit)}>
                  {currencySymbol}
                  {formatNumber(portfolioValue, true, decimalPoints)}
                </Typography>
                </Box>

              </Box>
            </Grid>
            {/* Styled Portfolio Value and Total P/L */}
              {/* <Grid item xs={12}>
                <Box display="flex" gap={2} mt={1}>
                  <StatBox>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Portfolio Value
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={400}
                      color="success.main"
                    >
                      {currencySymbol}
                      {formatNumber(
                        portfolioValue,
                        isNumberFormatting,
                        decimalPoints,
                      )}
                    </Typography>
                  </StatBox>
                  <StatBox>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Total P/L
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight={400}
                      color={getColorByNum(totalPL)}
                    >
                      {currencySymbol}
                      {formatNumber(totalPL, isNumberFormatting, decimalPoints)}
                    </Typography>
                  </StatBox> 
                </Box>
              </Grid> */}
          </Grid>
          <RealisedPopover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            handleClose={handlePopoverClose}
            realizedProfit={realisedUnrealisedData?.calculateTodaysRealisedProfit || 0}
            realizedLoss={realisedUnrealisedData?.calculateTodaysRealisedLoss || 0}
            unrealizedProfit={realisedUnrealisedData?.calculateTodaysUnrealisedProfit || 0}
            unrealizedLoss={realisedUnrealisedData?.calculateTodaysUnrealisedLoss || 0}
            currencySymbol={currencySymbol}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default PortfolioStatsCard;
